import React, { useRef, useState } from "react";
import { api, fileUrl } from "../lib/api";
import { Upload, X, Video, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "./ui/button";

export function VideoUploader({ onUploaded, testId = "video-uploader" }) {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [preview, setPreview] = useState(null);
  const [videoPath, setVideoPath] = useState(null);
  const [duration, setDuration] = useState(0);

  const handleFile = async (file) => {
    if (!file) return;
    if (!file.type.startsWith("video/")) {
      toast.error("Please upload a video file");
      return;
    }
    // Check duration client-side
    const videoEl = document.createElement("video");
    videoEl.preload = "metadata";
    videoEl.onloadedmetadata = async () => {
      window.URL.revokeObjectURL(videoEl.src);
      if (videoEl.duration > 33) {
        toast.error(`Video is ${Math.round(videoEl.duration)}s long. Maximum is 30 seconds — please trim your clip.`);
        return;
      }
      setDuration(videoEl.duration);
      setPreview(URL.createObjectURL(file));
      setUploading(true);
      try {
        const formData = new FormData();
        formData.append("file", file);
        const res = await api.post("/upload", formData, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        setVideoPath(res.data.path);
        onUploaded?.(res.data.path);
        toast.success("Video uploaded");
      } catch (e) {
        toast.error(e.response?.data?.detail || "Upload failed");
      } finally {
        setUploading(false);
      }
    };
    videoEl.src = URL.createObjectURL(file);
  };

  const clear = () => {
    setPreview(null);
    setVideoPath(null);
    onUploaded?.(null);
    if (inputRef.current) inputRef.current.value = "";
  };

  return (
    <div data-testid={testId} className="border border-dashed border-border bg-secondary/30 p-6">
      {!preview ? (
        <div
          className="text-center py-10 cursor-pointer hover:bg-secondary/40 transition-colors"
          onClick={() => inputRef.current?.click()}
          data-testid={`${testId}-drop`}
        >
          <Video className="w-10 h-10 mx-auto text-muted-foreground mb-3" />
          <p className="font-serif text-lg mb-1">Upload your video</p>
          <p className="label-mono text-muted-foreground">MP4 · MOV · WEBM · MAX 30 SECONDS</p>
        </div>
      ) : (
        <div className="space-y-4">
          <div className="relative bg-black">
            <video src={preview} controls className="w-full max-h-[400px] mx-auto" />
            {uploading && (
              <div className="absolute inset-0 bg-background/70 flex items-center justify-center">
                <div className="text-center">
                  <Loader2 className="w-8 h-8 animate-spin mx-auto mb-2" />
                  <p className="label-mono">Uploading…</p>
                </div>
              </div>
            )}
          </div>
          <div className="flex items-center justify-between">
            <div className="label-mono text-muted-foreground">
              {duration.toFixed(1)}s · {videoPath ? "Uploaded" : "Pending"}
            </div>
            <Button
              variant="outline"
              size="sm"
              className="rounded-sm"
              onClick={clear}
              data-testid={`${testId}-clear`}
            >
              <X className="w-4 h-4 mr-1" /> Remove
            </Button>
          </div>
        </div>
      )}
      <input
        ref={inputRef}
        type="file"
        accept="video/*"
        className="hidden"
        onChange={(e) => handleFile(e.target.files?.[0])}
        data-testid={`${testId}-input`}
      />
    </div>
  );
}

export function ImageUploader({ onUploaded, testId = "image-uploader", label = "Upload image" }) {
  const inputRef = useRef(null);
  const [preview, setPreview] = useState(null);
  const [uploading, setUploading] = useState(false);

  const handleFile = async (file) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast.error("Please upload an image");
      return;
    }
    setPreview(URL.createObjectURL(file));
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const res = await api.post("/upload", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      onUploaded?.(res.data.path);
      toast.success("Image uploaded");
    } catch (e) {
      toast.error("Upload failed");
    } finally {
      setUploading(false);
    }
  };

  return (
    <div data-testid={testId} className="border border-dashed border-border bg-secondary/30 p-4">
      {preview ? (
        <div className="flex items-center gap-4">
          <img src={preview} alt="preview" className="w-24 h-24 object-cover border border-border" />
          <div className="flex-1">
            <p className="label-mono text-muted-foreground">
              {uploading ? "Uploading…" : "Uploaded"}
            </p>
            <Button variant="outline" size="sm" className="rounded-sm mt-2" onClick={() => inputRef.current?.click()}>
              Change
            </Button>
          </div>
        </div>
      ) : (
        <div
          className="text-center py-6 cursor-pointer"
          onClick={() => inputRef.current?.click()}
        >
          <Upload className="w-6 h-6 mx-auto text-muted-foreground mb-2" />
          <p className="label-mono text-muted-foreground">{label}</p>
        </div>
      )}
      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={(e) => handleFile(e.target.files?.[0])}
        data-testid={`${testId}-input`}
      />
    </div>
  );
}
